import { AppState } from '../store';
import { ThunkNoArgs, ThunkWithArgs } from 'state/StateTypes';
import { Dispatch } from 'redux';
import * as assetTypes from 'state/assets/AssetTypes';
import * as actionTypes from './AssetActionTypes';
import FetchWrapper from 'Api/FetchWrapper';
import TokenManager from 'Api/TokenManager';
import { selectWaveforms, selectSensations } from './AssetSelectors';
import { SubscriptionTiers } from 'state/subscription/SubscriptionTypes';
/**
 * @ThunkWithArgs
 * <typdefs for params, typedefs for payload>
 * @ThunkNoArgs
 * <typedefs for payload>
 * --------------------------------------------
 * 2 separate types are used to avoid unnecessary null checks for actionCreator args
 */

export const getAssets: ThunkNoArgs<assetTypes.Assets> = () => async (dispatch: Dispatch) => {
   const token: string | null = await TokenManager.getAccessToken();
   if (!token) throw new Error('Token is either missing or invalid');

   const res = await FetchWrapper({
      path: 'asset',
      mode: 'cors',
      method: 'GET',
      token,
   });
   const { assets }: assetTypes.GetAssetsResponse = await res.json();
   const sensations: assetTypes.Asset[] = [];
   const waveforms: assetTypes.Asset[] = [];

   assets.forEach(asset => (asset.type === assetTypes.AssetTypes.Sensation ? sensations.push(asset) : waveforms.push(asset)));
   return dispatch({ type: actionTypes.GET_ASSETS, payload: { sensations, waveforms } });
};

export const getAsset: ThunkWithArgs<string, assetTypes.Asset> = (id: string) => async (dispatch: Dispatch) => {
   const token: string | null = await TokenManager.getAccessToken();
   if (!token) throw new Error('Token is either missing or invalid');
   const res = await FetchWrapper({
      path: `asset/${id}`,
      mode: 'cors',
      method: 'GET',
      token,
   });

   const { asset }: assetTypes.GetAssetResponse = await res.json();
   return dispatch({ type: actionTypes.SET_SELECTED_ASSET, payload: asset });
};

export const createAsset: ThunkWithArgs<Partial<assetTypes.CreateAssetRequest>, assetTypes.Asset> = asset => async (dispatch: Dispatch) => {
   const token: string | null = await TokenManager.getAccessToken();
   if (!token) throw new Error('Token is either missing or invalid');
   const res = await FetchWrapper({
      path: 'asset',
      mode: 'cors',
      method: 'POST',
      token,
      body: asset,
   });

   const { createdAsset }: assetTypes.CreateAssetResponse = await res.json();
   return dispatch({ type: actionTypes.SET_SELECTED_ASSET, payload: createdAsset });
};

export const updateAsset: ThunkWithArgs<assetTypes.UpdateAssetRequest, assetTypes.Asset> = ({
   active,
   bytes,
   description,
   name,
   tier,
   _id,
}) => async (dispatch: Dispatch, getState: () => AppState) => {
   const token: string | null = await TokenManager.getAccessToken();
   if (!token) throw new Error('Token is either missing or invalid');

   const res = await FetchWrapper({
      path: `asset/${_id}`,
      mode: 'cors',
      method: 'PATCH',
      token,
      body: {
         active,
         bytes,
         description,
         name,
         tier,
      },
   });

   const { updatedAsset }: assetTypes.UpdateAssetResponse = await res.json();
   //if asset being updated is one being viewed, ensure it's updated in redux state
   if (getState().currentAssets.selectedAsset) {
      dispatch({ type: actionTypes.SET_SELECTED_ASSET, payload: updatedAsset });
   }
   return dispatch({ type: actionTypes.UPDATE_ASSET, payload: updatedAsset });
};

export const deleteAsset: ThunkWithArgs<assetTypes.Asset, assetTypes.Asset | null> = asset => async (
   dispatch: Dispatch,
   getState: () => AppState
) => {
   const token: string | null = await TokenManager.getAccessToken();
   if (!token) throw new Error('Token is either missing or invalid');

   await FetchWrapper({
      path: `/asset/${asset._id}`,
      mode: 'cors',
      method: 'DELETE',
      token,
   });

   if (getState().currentAssets.selectedAsset?._id === asset._id) {
      dispatch({ type: actionTypes.SET_SELECTED_ASSET, payload: null });
   }
   return dispatch({ type: actionTypes.DELETE_ASSET, payload: asset });
};

//Note that the filtering is subject to change. Right now, since there's only a Free and Paid tier, a boolean value is used to determine the Tier:
//false === Free
//true === Paid
//null (default) === no filter applied
type FilterArgs = { active: assetTypes.Filter; tier: assetTypes.Filter; target: assetTypes.StateItems };
export const filterAssets: ThunkWithArgs<FilterArgs, assetTypes.Asset[] | null> = ({ active, tier, target }) => async (
   dispatch: Dispatch,
   getState: () => AppState
) => {
   let filteredAssets: assetTypes.Asset[] | null = [];
   //because tier enum is not passed in, it must be determined here
   let desiredTier: SubscriptionTiers | null = null;
   if (tier === SubscriptionTiers.Paid) {
      desiredTier = SubscriptionTiers.Paid;
   } else if (tier === SubscriptionTiers.Free) {
      desiredTier = SubscriptionTiers.Free;
   }
   const assets: assetTypes.Asset[] | null = target === 'waveforms' ? selectWaveforms(getState()) : selectSensations(getState());
   //not a huge fan of this logic ¯\_(ツ)_/¯
   if (assets) {
      for (let i = 0; i < assets.length; i++) {
         //NO FILTERS
         if (active === null && desiredTier === null) {
            filteredAssets.push(assets[i]);
            //BOTH FILTERS
         } else if (active !== null && desiredTier !== null) {
            if (assets[i].active === active && assets[i].tier === desiredTier) {
               filteredAssets.push(assets[i]);
            }
            //JUST ACTIVE FILTER
         } else if (active !== null && desiredTier === null) {
            if (assets[i].active === active) {
               filteredAssets.push(assets[i]);
            }
            //JUST TIER FILTER
         } else {
            if (assets[i].tier === desiredTier) {
               filteredAssets.push(assets[i]);
            }
         }
      }
   } else {
      filteredAssets = null;
   }
   return dispatch({ type: actionTypes[target === 'waveforms' ? 'FILTER_WAVEFORMS' : 'FILTER_SENSATIONS'], payload: filteredAssets });
};
